import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../hooks/useAuth';
import * as dashApi from '../api/dashboard';
import type { DashboardData } from '../types';
import AppHeader from '../components/AppHeader';
import { STATUS_COLORS } from '../lib/defaultBatch';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    dashApi
      .getDashboard()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setErr('Failed to load dashboard.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  return (
    <>
      <AppHeader subtitle="Dashboard" />
      <div className="main">
        <div className="page-eyebrow">Overview</div>
        <div className="page-title">Workspace dashboard</div>
        <div className="page-sub">Snapshot across all batches in this workspace.</div>

        {err && <div className="login-error">{err}</div>}
        {!data && !err && <div style={{ color: 'var(--ink3)', fontSize: 13 }}>Loading…</div>}

        {data && (
          <>
            <div className="dash-totals">
              <div className="dash-card">
                <div className="dash-card-title">Batches</div>
                <div className="stat-value">{data.totals.batches}</div>
              </div>
              <div className="dash-card">
                <div className="dash-card-title">Ingredients in library</div>
                <div className="stat-value">{data.totals.ingredients}</div>
              </div>
              <div className="dash-card">
                <div className="dash-card-title">Attachments</div>
                <div className="stat-value">{data.totals.attachments}</div>
              </div>
            </div>

            <div className="dash-grid">
              <div className="dash-card">
                <div className="dash-card-title">Batches by status</div>
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={Object.entries(data.status_counts).map(([k, v]) => ({
                          name: k,
                          value: v,
                        }))}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={45}
                        outerRadius={80}
                      >
                        {Object.keys(data.status_counts).map((k) => (
                          <Cell key={k} fill={STATUS_COLORS[k] ?? '#bbb'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dash-card">
                <div className="dash-card-title">Batches created per week (last 12 weeks)</div>
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={data.batches_per_week.map((w) => ({
                        week: new Date(w.week_start).toISOString().slice(5, 10),
                        count: w.count,
                      }))}
                    >
                      <CartesianGrid stroke="#E2DED8" strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#999890' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#999890' }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#5C7A52" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dash-card" style={{ gridColumn: 'span 2' }}>
                <div className="dash-card-title">Recent activity</div>
                {data.recent_activity.length === 0 && (
                  <div style={{ color: 'var(--ink3)', fontSize: 12 }}>No recent activity.</div>
                )}
                {data.recent_activity.map((a, i) => (
                  <div className="activity-row" key={i}>
                    <span style={{ color: a.color ?? 'var(--ink2)', fontWeight: 600 }}>
                      {a.display_name ?? 'someone'}
                    </span>{' '}
                    <span>{describeAction(a.action)}</span>{' '}
                    {a.batch_id_display && <span>· {a.batch_id_display}</span>}
                    <span className="activity-when">
                      {new Date(a.occurred_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function describeAction(a: string): string {
  switch (a) {
    case 'batch.write':
      return 'edited a batch';
    case 'batch.delete':
      return 'deleted a batch';
    case 'batch.restore':
      return 'restored a version';
    case 'attachment.upload':
      return 'uploaded a photo';
    default:
      return a;
  }
}
